import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { contactsApi, importExportApi } from '../api';
import { Plus, Pencil, Trash2, Upload, Download } from 'lucide-react';
import ContactModal from '../components/ContactModal';
import ImportModal from '../components/ImportModal';

export default function ContactsPage() {
  const [contacts, setContacts] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [page, setPage] = useState(1);
  const limit = 20;

  const load = async () => {
    setLoading(true);
    try {
      const res = await contactsApi.list({ search: search || undefined, page, limit });
      setContacts(res.data.data);
      setTotal(res.data.total);
    } catch (e) {
      console.error('Load contacts error:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, page]);
  useEffect(() => { setPage(1); }, [search]);

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить контакт?')) return;
    try {
      await contactsApi.remove(id);
      load();
    } catch (e) {
      alert('Ошибка удаления');
    }
  };

  const handleExport = async () => {
    try {
      const res = await importExportApi.exportContacts();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const d = new Date();
      a.download = `contacts_${d.toISOString().slice(0, 10)}_${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Ошибка экспорта');
    }
  };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Контакты</h2>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-secondary" onClick={() => setShowImport(true)}>
            <Upload size={18} /> Импорт
          </button>
          <button className="btn btn-secondary" onClick={handleExport}>
            <Download size={18} /> Экспорт
          </button>
          <button className="btn btn-primary" onClick={() => { setEditing(null); setShowModal(true); }}>
            <Plus size={18} /> Добавить контакт
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="toolbar" style={{ margin: 0, width: '100%' }}>
            <input
              className="search-input"
              placeholder="Поиск по контактам..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ФИО</th>
                    <th>Должность</th>
                    <th>Компания</th>
                    <th>Телефон</th>
                    <th>Email</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {contacts.length === 0 && (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>Контакты не найдены</td></tr>
                  )}
                  {contacts.map(c => (
                    <tr key={c.id}>
                      <td style={{ fontWeight: 600 }}><Link to={`/contacts/${c.id}`} className="detail-link">{c.firstName} {c.lastName}</Link></td>
                      <td className="text-muted">{c.position || '—'}</td>
                      <td>{c.company?.name || '—'}</td>
                      <td>{c.phones?.[0] || '—'}</td>
                      <td>{c.emails?.[0] || '—'}</td>
                      <td>
                        <div className="flex gap-2">
                          <button className="header-icon-btn" style={{ width: 32, height: 32 }} onClick={() => { setEditing(c); setShowModal(true); }}>
                            <Pencil size={14} />
                          </button>
                          <button className="header-icon-btn" style={{ width: 32, height: 32, color: 'var(--color-danger)' }} onClick={() => handleDelete(c.id)}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        <div className="card-footer" style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border-soft)' }}>
          <div className="pagination">
            <button className="btn btn-secondary btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
            <div className="flex gap-2" style={{ margin: '0 16px' }}>
              <span style={{ fontSize: 14, fontWeight: 600 }}>Страница {page}</span>
            </div>
            <button className="btn btn-secondary btn-sm" disabled={page * limit >= total} onClick={() => setPage(p => p + 1)}>Далее</button>
          </div>
        </div>
      </div>

      {showModal && (
        <ContactModal
          contact={editing}
          onClose={() => setShowModal(false)}
          onSave={() => { setShowModal(false); load(); }}
        />
      )}

      {showImport && (
        <ImportModal
          entityType="contacts"
          onImport={(file) => importExportApi.importContacts(file)}
          onClose={() => { setShowImport(false); load(); }}
        />
      )}
    </div>
  );
}
